import { useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, LockKeyhole } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { useHasPermission } from "../../context/AuthContext";
import { useAgents } from "../home/queries";
import { AgentSelect } from "./agent-select";
import { callAnalyticsApi, errorText } from "./api";
import { ConfirmDialog } from "./confirm-dialog";
import { FeatureSettingsEditor } from "./feature-settings-editor";
import { FEATURE_LABELS } from "./format";
import { ScorecardEditor } from "./scorecard-editor";
import type { ConfigResponse, FeatureInfo, FeatureSettings, ScopeDoc } from "./types";
import { FEATURE_KEYS } from "./types";

// One shared object: the editors reset their draft when this reference changes.
const EMPTY: FeatureSettings = {};
const CONFIG_KEY = ["call-analytics", "config"];

function Section({ title, description, children }: { title: string; description?: ReactNode; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-2xl border border-line bg-elevated p-4">
      <div>
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        {description ? <p className="text-sm text-ink-3">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

function overrideSummary(doc: ScopeDoc): string {
  const parts = FEATURE_KEYS.flatMap((key) => {
    const s = doc.features[key];
    const bits: string[] = [];
    if (s?.enabled === true) bits.push("opted in");
    if (s?.enabled === false) bits.push("opted out");
    if (s?.sample_pct != null) bits.push(`${s.sample_pct}%`);
    return bits.length ? [`${FEATURE_LABELS[key]}: ${bits.join(", ")}`] : [];
  });
  return parts.join(" · ") || "No feature overrides";
}

function AgentOverrideEditor({
  agentId,
  config,
  features,
  readOnly,
  onDone,
}: {
  agentId: string;
  config: ConfigResponse;
  features: FeatureInfo[] | undefined;
  readOnly: boolean;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const agentConfig = useQuery({
    queryKey: ["call-analytics", "agent-config", agentId],
    queryFn: () => callAnalyticsApi.agentConfig(agentId),
  });
  const original = agentConfig.data?.override ?? EMPTY;
  const platform = config.platform?.features ?? EMPTY;
  if (agentConfig.isLoading) return <Loader2 className="animate-spin text-brand" aria-label="Loading agent settings" />;
  if (agentConfig.isError) return <p role="alert" className="text-sm text-danger">{errorText(agentConfig.error)}</p>;
  return (
    <FeatureSettingsEditor
      scope="agent"
      features={features}
      original={original}
      platform={platform}
      effective={agentConfig.data?.effective}
      readOnly={readOnly}
      onSave={async (features) => {
        await callAnalyticsApi.saveAgentConfig(agentId, features);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["call-analytics", "agent-config", agentId] }),
          queryClient.invalidateQueries({ queryKey: CONFIG_KEY }),
        ]);
        onDone();
      }}
    />
  );
}

function AgentOverrides({ config, features, readOnly }: { config: ConfigResponse; features: FeatureInfo[] | undefined; readOnly: boolean }) {
  const queryClient = useQueryClient();
  const agents = useAgents();
  const names = useMemo(() => new Map((agents.data ?? []).map((a) => [a.agent_id, a.agent_name || a.name || a.agent_id])), [agents.data]);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState("");
  const [removing, setRemoving] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const existing = config.agents.map((doc) => doc.scope_id);
  const open = editing ?? (adding || null);

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    setError(null);
    try {
      await callAnalyticsApi.deleteAgentConfig(removing);
      await queryClient.invalidateQueries({ queryKey: CONFIG_KEY });
      if (editing === removing) setEditing(null);
      setRemoving(null);
    } catch (e) {
      setError(errorText(e, "The override could not be removed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {config.agents.length ? (
        <ul className="divide-y divide-line rounded-xl border border-line">
          {config.agents.map((doc) => (
            <li key={doc.scope_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-ink">{names.get(doc.scope_id) ?? doc.scope_id}</div>
                <div className="text-xs text-ink-3">{overrideSummary(doc)}</div>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => { setAdding(""); setEditing(doc.scope_id); }} className="rounded-lg border border-line px-3 py-1 text-sm text-ink hover:bg-soft">
                  {readOnly ? "View" : "Edit"}
                </button>
                {!readOnly ? (
                  <button type="button" onClick={() => setRemoving(doc.scope_id)} className="rounded-lg border border-line px-3 py-1 text-sm text-danger hover:bg-danger-soft">Remove</button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-ink-3">No agent has its own settings; every agent follows the organization.</p>}

      {!readOnly ? (
        <AgentSelect label="Add an override for" value={adding} exclude={existing} onChange={(id) => { setEditing(null); setAdding(id); }} />
      ) : null}

      {open ? (
        <div className="space-y-2 rounded-xl border border-line bg-canvas p-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ink">{names.get(open) ?? open}</h3>
            <button type="button" onClick={() => { setEditing(null); setAdding(""); }} className="text-sm text-ink-3 hover:text-ink">Close</button>
          </div>
          <AgentOverrideEditor
            key={open}
            agentId={open}
            config={config}
            features={features}
            readOnly={readOnly}
            onDone={() => { setAdding(""); setEditing(open); }}
          />
        </div>
      ) : null}

      {removing ? (
        <ConfirmDialog title="Remove this agent's override?" confirmLabel="Remove" danger busy={busy} onConfirm={remove} onClose={() => { setRemoving(null); setError(null); }}>
          <p>{names.get(removing) ?? removing} will follow the organization's settings again.</p>
          {error ? <p role="alert" className="text-danger">{error}</p> : null}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

function Scorecards({ readOnly }: { readOnly: boolean }) {
  const queryClient = useQueryClient();
  const [agentId, setAgentId] = useState("");
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scorecard = useQuery({
    queryKey: ["call-analytics", "scorecard", agentId],
    queryFn: () => callAnalyticsApi.scorecard(agentId || null),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["call-analytics", "scorecard"] });
  const agentOwn = Boolean(agentId && scorecard.data?.agent);

  const remove = async () => {
    setError(null);
    try {
      await callAnalyticsApi.deleteAgentScorecard(agentId);
      await refresh();
      setRemoving(false);
    } catch (e) {
      setError(errorText(e, "The agent scorecard could not be removed."));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <AgentSelect label="Scorecard for" value={agentId} onChange={setAgentId} includeAll />
        {agentOwn && !readOnly ? (
          <button type="button" onClick={() => setRemoving(true)} className="h-9 rounded-lg border border-line px-3 text-sm text-danger hover:bg-danger-soft">Remove agent scorecard</button>
        ) : null}
      </div>
      <p className="text-xs text-ink-3">{agentId ? "Leave the organization scorecard in place and give this agent its own." : "“All agents” edits the organization scorecard."}</p>
      {scorecard.isLoading ? (
        <Loader2 className="animate-spin text-brand" aria-label="Loading scorecard" />
      ) : scorecard.isError || !scorecard.data ? (
        <p role="alert" className="text-sm text-danger">{errorText(scorecard.error, "The scorecard could not be loaded.")}</p>
      ) : (
        <ScorecardEditor
          key={agentId || "organization"}
          effective={scorecard.data.effective}
          readOnly={readOnly}
          saveLabel={agentId && !agentOwn ? "Create agent scorecard" : "Save scorecard"}
          onSave={async (next) => {
            if (agentId) await callAnalyticsApi.saveAgentScorecard(agentId, next);
            else await callAnalyticsApi.saveOrganizationScorecard(next);
            await refresh();
          }}
        />
      )}
      {removing ? (
        <ConfirmDialog title="Remove this agent's scorecard?" confirmLabel="Remove" danger onConfirm={remove} onClose={() => { setRemoving(false); setError(null); }}>
          <p>The agent will be scored with the organization scorecard again.</p>
          {error ? <p role="alert" className="text-danger">{error}</p> : null}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

export function CallAnalyticsSettingsPage() {
  const queryClient = useQueryClient();
  const hasConfigure = useHasPermission("call_analytics.configure");
  const config = useQuery({ queryKey: CONFIG_KEY, queryFn: callAnalyticsApi.config });
  const features = useQuery({ queryKey: ["call-analytics", "features"], queryFn: callAnalyticsApi.features, staleTime: 5 * 60_000 });
  const orgFeatures = config.data?.organization?.features ?? EMPTY;
  const platform = config.data?.platform?.features ?? EMPTY;

  const header = (
    <header className="flex items-center gap-3">
      <Link to="/call-analytics" aria-label="Back to Call Analytics" className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-ink-3 hover:text-ink">
        <ArrowLeft className="h-4 w-4" aria-hidden />
      </Link>
      <div>
        <h1 className="text-xl font-semibold text-ink">Call Analytics settings</h1>
        <p className="text-sm text-ink-3">Which analyses run, on what share of calls, and how calls are scored.</p>
      </div>
    </header>
  );

  if (config.isLoading) {
    return <div className="space-y-4 px-4 pb-6 pt-3 sm:px-5 sm:pt-4 md:px-6">{header}<Loader2 className="animate-spin text-brand" aria-label="Loading settings" /></div>;
  }
  if (config.isError || !config.data) {
    return (
      <div className="space-y-4 px-4 pb-6 pt-3 sm:px-5 sm:pt-4 md:px-6">
        {header}
        <p role="alert" className="rounded-xl border border-danger/30 bg-danger-soft p-4 text-sm text-danger">{errorText(config.error, "Settings could not be loaded.")}</p>
      </div>
    );
  }

  const data = config.data;
  // The server decides; the permission only hides controls it would refuse anyway.
  const readOnly = !(hasConfigure && data.can_configure && data.master_enabled);

  return (
    <div className="space-y-4 px-4 pb-6 pt-3 sm:px-5 sm:pt-4 md:px-6">
      {header}

      {readOnly && data.master_enabled ? (
        <p role="status" className="flex items-center gap-2 rounded-xl border border-line bg-soft px-3 py-2 text-sm text-ink-2">
          <LockKeyhole className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
          You can view these settings. Changing them needs the “Configure call analytics” permission.
        </p>
      ) : null}

      <Section title="Call Analytics for your organization">
        <div className="flex flex-wrap items-center gap-2 text-sm text-ink-2">
          <Badge tone={data.master_enabled ? "success" : "neutral"}>{data.master_enabled ? "Enabled" : "Not enabled"}</Badge>
          {data.master_enabled
            ? "TechLadder has enabled Call Analytics for your organization. Each analysed call is billed."
            : "TechLadder enables Call Analytics per organization. Until then nothing is analysed and nothing here can be changed."}
        </div>
      </Section>

      <Section
        title="Organization features"
        description="On turns a feature on for every agent. Off is a restriction no agent can undo. Not set leaves it off but lets an agent opt in."
      >
        <FeatureSettingsEditor
          scope="organization"
          features={features.data?.features}
          original={orgFeatures}
          platform={platform}
          effective={data.effective}
          readOnly={readOnly}
          onSave={async (features) => {
            await callAnalyticsApi.saveOrganizationConfig(features);
            await queryClient.invalidateQueries({ queryKey: CONFIG_KEY });
          }}
        />
      </Section>

      <Section title="Agent overrides" description="An agent can opt in where the organization has not set a feature, opt out, or sample fewer calls.">
        <AgentOverrides config={data} features={features.data?.features} readOnly={readOnly} />
      </Section>

      <Section title="QA scorecard" description="Criteria the QA analysis scores each call against, plus the claims, disclosures and competitors the compliance check looks for.">
        <Scorecards readOnly={readOnly} />
      </Section>
    </div>
  );
}
